import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Newspaper, Eye, Send, Clock, ChevronRight, 
  Menu, X, Search, Rss, Loader2, Download,
  TrendingUp, CloudSun, DollarSign, Zap, Sparkles, Sun, Moon, User, Bell, Crown, Trophy
} from "lucide-react";
import { BACKEND_URL, API } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useTheme } from "../context/ThemeContext";
import { useMember } from "../context/MemberContext";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [stats, setStats] = useState({ total_news: 0, total_views: 0 });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [installPrompt, setInstallPrompt] = useState(null);
  const [searchMode, setSearchMode] = useState('normal');
  const [breakingNews, setBreakingNews] = useState([]);
  const [rates, setRates] = useState(null);
  const [weather, setWeather] = useState(null);
  const [trendTags, setTrendTags] = useState([]);
  const [langOpen, setLangOpen] = useState(false);
  const tickerRef = useRef(null);
  const navigate = useNavigate();
  const { lang, setLang, t, LANGS } = useLang();
  const { theme, toggleTheme } = useTheme();
  const { member } = useMember();

  const categories = [
    { name: 'Gündem', key: 'gundem', color: 'hover:text-red-400' },
    { name: 'Ekonomi', key: 'ekonomi', color: 'hover:text-green-400' },
    { name: 'Spor', key: 'spor', color: 'hover:text-blue-400' },
    { name: 'Teknoloji', key: 'teknoloji', color: 'hover:text-blue-400' },
    { name: 'Kültür', key: 'kultur', color: 'hover:text-pink-400' },
    { name: 'Sağlık', key: 'saglik', color: 'hover:text-emerald-400' },
    { name: 'Dünya', key: 'dunya', color: 'hover:text-cyan-400' },
    { name: 'Bilim', key: 'bilim', color: 'hover:text-amber-400' },
  ];

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, newsRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/news?limit=8&language=${lang}`)
        ]);
        setStats(statsRes.data);
        setBreakingNews(newsRes.data.slice(0, 8));
      } catch (e) { console.error(e); }

      // Fetch market data (USD, EUR, BIST, Gold) from backend
      try {
        const mRes = await axios.get(`${API}/market`);
        setRates(mRes.data);
      } catch (e) { console.error('Market error:', e); }

      // Fetch weather (Istanbul) with language support
      try {
        const wRes = await axios.get('https://wttr.in/Istanbul?format=j1');
        const current = wRes.data.current_condition?.[0];
        if (current) {
          // wttr.in supports lang_tr, lang_en etc.
          const langKey = `lang_${lang}`;
          const weatherDesc = current[langKey]?.[0]?.value || current.weatherDesc?.[0]?.value || '';
          setWeather({ temp: current.temp_C, desc: weatherDesc });
        }
      } catch (e) { console.error('Weather error:', e); }

      // Fetch AI trends
      try {
        const tRes = await axios.get(`${API}/ai/trends?size=5`);
        const tags = (tRes.data.stories || []).map(s => s.name).filter(Boolean);
        setTrendTags(tags.slice(0, 5));
      } catch (e) { console.error('Trends error:', e); }
    };
    fetchAll();

    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    const handleBeforeInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => { clearInterval(timer); window.removeEventListener('beforeinstallprompt', handleBeforeInstall); };
  }, [lang]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      if (searchMode === 'ai') {
        const response = await axios.get(`${API}/ai/search?q=${encodeURIComponent(query)}&size=5`);
        setSearchResults(response.data.map(a => ({ id: null, title: a.title, summary: a.description, image_url: a.image_url, category: a.source, url: a.url, is_external: true })));
      } else {
        const response = await axios.get(`${API}/news/search?q=${encodeURIComponent(query)}&limit=5`);
        setSearchResults(response.data);
      }
    } catch (e) { console.error(e); } finally { setSearching(false); }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`); }
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') setInstallPrompt(null);
  };

  const formatDate = (date) => {
    const daysMap = {
      tr: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
      en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      ar: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
      ru: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота']
    };
    const monthsMap = {
      tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
      en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
      ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
    };
    const days = daysMap[lang] || daysMap.tr;
    const months = monthsMap[lang] || monthsMap.tr;
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Top Info Bar: Date + Rates + Weather */}
      <div className="bg-[#070708] border-b border-zinc-800/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="hidden md:flex items-center justify-between h-9 text-[11px]">
            <div className="flex items-center gap-3 text-zinc-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(currentTime)}
              </span>
              {weather && (
                <>
                  <span className="text-zinc-700">|</span>
                  <span className="flex items-center gap-1">
                    <CloudSun className="w-3 h-3 text-amber-500" />
                    <span className="text-zinc-300">{weather.temp}°C</span>
                    <span className="text-zinc-500">{weather.desc}</span>
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              {rates && (
                <div className="flex items-center gap-2.5 text-zinc-400">
                  {rates.bist100 && (
                    <>
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-amber-500" />
                        <span className="text-zinc-300">BIST</span> {rates.bist100}
                        {rates.bist100_change != null && (
                          <span className={rates.bist100_change >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                            {rates.bist100_change >= 0 ? '+' : ''}{rates.bist100_change}%
                          </span>
                        )}
                      </span>
                      <span className="text-zinc-700">|</span>
                    </>
                  )}
                  {rates.gold_try && (
                    <>
                      <span className="flex items-center gap-1"><span className="text-amber-400">Au</span> {rates.gold_try} TL/gr</span>
                      <span className="text-zinc-700">|</span>
                    </>
                  )}
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3 text-green-500" /><span className="text-zinc-300">USD</span> {rates.usd_try} TL</span>
                  <span className="text-zinc-700">|</span>
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3 text-blue-500" /><span className="text-zinc-300">EUR</span> {rates.eur_try} TL</span>
                </div>
              )}
              <span className="text-zinc-700">|</span>
              <a href="https://t.me/loftpress" target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-blue-400 transition-colors">
                <Send className="w-3 h-3" />
              </a>
              <a href={`${BACKEND_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-orange-400 transition-colors">
                <Rss className="w-3 h-3" />
              </a>
              {/* Theme Toggle */}
              <button onClick={toggleTheme} className="text-zinc-500 hover:text-amber-400 transition-colors" data-testid="theme-toggle" title={theme === 'dark' ? 'Aydınlık mod' : 'Karanlık mod'}>
                {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              </button>
              {/* Language Selector */}
              <div className="relative">
                <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors text-[11px] font-medium" data-testid="lang-selector">
                  {LANGS.find(l => l.code === lang)?.flag} {lang.toUpperCase()}
                  <ChevronRight className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-90' : ''}`} />
                </button>
                {langOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden shadow-xl z-50">
                    {LANGS.map(l => (
                      <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-zinc-800 transition-colors ${lang === l.code ? 'text-amber-500' : 'text-zinc-300'}`} data-testid={`lang-${l.code}`}>
                        <span>{l.flag}</span> {l.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breaking News Ticker */}
      {breakingNews.length > 0 && (
        <div className="bg-[#0c0c0d] border-b border-zinc-800/30 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 flex items-center h-8">
            <div className="flex-shrink-0 flex items-center gap-1.5 mr-3">
              <Zap className="w-3.5 h-3.5 text-red-500 animate-pulse" />
              <span className="text-[11px] font-bold text-red-500 uppercase tracking-wider">{t('breakingNews')}</span>
            </div>
            <div className="overflow-hidden flex-1" ref={tickerRef}>
              <div className="flex animate-ticker whitespace-nowrap">
                {[...breakingNews, ...breakingNews].map((item, i) => (
                  <Link key={i} to={`/haber/${item.slug || item.id}`} className="inline-flex items-center gap-2 text-[11px] text-zinc-400 hover:text-amber-400 transition-colors mr-8">
                    <span className="w-1 h-1 bg-amber-500 rounded-full flex-shrink-0" />
                    <span className="truncate">{item.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Header + Categories */}
      <div className="bg-[#0a0a0b]/95 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-400 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="18" rx="1.5" />
                  <rect x="8" y="3" width="13" height="18" rx="1.5" />
                  <line x1="11" y1="7" x2="18" y2="7" />
                  <line x1="11" y1="10.5" x2="17" y2="10.5" />
                  <line x1="11" y1="14" x2="16" y2="14" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white leading-none tracking-tight">loft<span className="text-amber-500">.press</span></span>
                <span className="text-[9px] text-zinc-500 tracking-[0.15em] uppercase mt-0.5">Bağımsız Haber</span>
              </div>
            </Link>
            
            {/* Categories - Center */}
            <nav className="hidden lg:flex items-center">
              <div className="flex items-center bg-zinc-900/50 border border-zinc-800/40 rounded-full px-1 py-0.5">
                <Link to="/" className="px-3 py-1.5 text-xs font-medium text-amber-500 hover:bg-amber-500/10 rounded-full transition-colors whitespace-nowrap">
                  {t('home')}
                </Link>
                {categories.map((cat) => (
                  <Link key={cat.name} to={`/?category=${encodeURIComponent(cat.name)}`} className={`px-2.5 py-1.5 text-xs font-medium text-zinc-400 ${cat.color} hover:bg-zinc-800/70 rounded-full transition-colors whitespace-nowrap`}>
                    {t(cat.key)}
                  </Link>
                ))}
                <Link to="/ai-asistan" className="px-2.5 py-1.5 text-xs font-medium text-blue-400 hover:bg-blue-500/10 rounded-full transition-colors whitespace-nowrap flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />AI
                </Link>
                <Link to="/liderlik" className="px-2.5 py-1.5 text-xs font-medium text-amber-400 hover:bg-amber-500/10 rounded-full transition-colors whitespace-nowrap flex items-center gap-1" data-testid="header-leaderboard">
                  <Trophy className="w-3 h-3" />{t('leaderboard')}
                </Link>
              </div>
            </nav>

            {/* Right: Member + Search + Install */}
            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
              {member ? (
                <>
                  {member.role !== "premium" && (
                    <Link to="/premium" className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-full text-xs transition-colors" data-testid="premium-btn">
                      <Crown className="w-3.5 h-3.5" /> Premium
                    </Link>
                  )}
                  <Link to="/profil" className="relative p-2 text-zinc-400 hover:text-amber-400 transition-colors" data-testid="notif-btn">
                    <Bell className="w-4 h-4" />
                  </Link>
                  <Link to="/profil" className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-xs font-medium transition-colors" data-testid="member-profile-btn">
                    {member.picture ? <img src={member.picture} alt="" className="w-5 h-5 rounded-full" /> : <User className="w-3.5 h-3.5" />}
                    {member.name?.split(' ')[0]}
                  </Link>
                </>
              ) : (
                <Link to="/uye" className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-700/50 rounded-full text-zinc-400 hover:text-white text-xs font-medium transition-colors" data-testid="member-login-btn">
                  <User className="w-3.5 h-3.5" />
                  {t('login')}
                </Link>
              )}
              {installPrompt && (
                <button onClick={handleInstall} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-full text-xs transition-colors" data-testid="pwa-install-btn">
                  <Download className="w-3.5 h-3.5" />
                  {t('install')}
                </button>
              )}
              <button onClick={() => setSearchOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-700/50 rounded-full text-zinc-400 hover:text-white transition-all text-sm" data-testid="search-button">
                <Search className="w-4 h-4" />
                <span className="hidden xl:inline">{t('search')}</span>
              </button>
            </div>

            <button className="lg:hidden p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* AI Trend Tags */}
      {trendTags.length > 0 && (
        <div className="bg-[#0a0a0b] border-b border-zinc-800/20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="hidden md:flex items-center gap-2 h-8 overflow-x-auto scrollbar-hide">
              <TrendingUp className="w-3 h-3 text-blue-500 flex-shrink-0" />
              <span className="text-[10px] text-blue-400 font-semibold flex-shrink-0">{t('trend')}</span>
              {trendTags.map((tag, i) => (
                <span key={i} className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] rounded-full whitespace-nowrap">{tag.length > 35 ? tag.slice(0, 35) + '...' : tag}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0a0a0b] border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <nav className="flex flex-col gap-1">
              <Link to="/" className="px-3 py-2.5 text-zinc-200 hover:text-amber-400 hover:bg-zinc-800/50 rounded-lg transition-colors text-sm" onClick={() => setMenuOpen(false)}>{t('home')}</Link>
              <div className="px-3 py-1 text-[10px] text-zinc-500 uppercase tracking-wider">{t('categories_title')}</div>
              <div className="grid grid-cols-2 gap-1 px-1">
                {categories.map((cat) => (
                  <Link key={cat.name} to={`/?category=${encodeURIComponent(cat.name)}`} className="px-3 py-2 text-zinc-300 hover:bg-zinc-800/50 rounded-lg transition-colors text-sm" onClick={() => setMenuOpen(false)}>{t(cat.key)}</Link>
                ))}
              </div>
              <div className="h-px bg-zinc-800 my-1" />
              {weather && (
                <div className="px-3 py-2 text-xs text-zinc-400 flex items-center gap-2">
                  <CloudSun className="w-4 h-4 text-amber-500" />{weather.temp}°C - {weather.desc}
                </div>
              )}
              {rates && (
                <div className="px-3 py-2 text-xs text-zinc-400 flex items-center gap-3 flex-wrap">
                  {rates.bist100 && <span>BIST: {rates.bist100}{rates.bist100_change != null && <span className={rates.bist100_change >= 0 ? ' text-emerald-400' : ' text-red-400'}> {rates.bist100_change >= 0 ? '+' : ''}{rates.bist100_change}%</span>}</span>}
                  {rates.gold_try && <span>{t('gold')}: {rates.gold_try} TL/gr</span>}
                  <span>USD: {rates.usd_try} TL</span>
                  <span>EUR: {rates.eur_try} TL</span>
                </div>
              )}
              <div className="h-px bg-zinc-800 my-1" />
              <div className="flex items-center gap-3 px-3 py-2">
                <a href="https://t.me/loftpress" target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-blue-400 text-sm flex items-center gap-1.5"><Send className="w-4 h-4" />Telegram</a>
                <a href={`${BACKEND_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-orange-400 text-sm flex items-center gap-1.5"><Rss className="w-4 h-4" />RSS</a>
                {installPrompt && <button onClick={handleInstall} className="text-amber-500 text-sm flex items-center gap-1.5"><Download className="w-4 h-4" />{t('install')}</button>}
              </div>
              <button onClick={() => { setMenuOpen(false); setSearchOpen(true); }} className="mx-1 mt-1 flex items-center justify-center gap-2 py-2.5 bg-zinc-800 text-zinc-300 rounded-lg text-sm"><Search className="w-4 h-4" />{t('search')}</button>
            </nav>
          </div>
        </div>
      )}

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20" data-testid="search-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
          <div className="relative w-full max-w-2xl mx-4">
            <div className="flex items-center gap-2 mb-2">
              <button onClick={() => { setSearchMode('normal'); setSearchResults([]); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${searchMode === 'normal' ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>{t('normalSearch')}</button>
              <button onClick={() => { setSearchMode('ai'); setSearchResults([]); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${searchMode === 'ai' ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`} data-testid="ai-search-toggle">{t('aiSearch')}</button>
            </div>
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input type="text" value={searchQuery} onChange={(e) => handleSearch(e.target.value)} placeholder={searchMode === 'ai' ? t('searchAI') : t('search')} className={`w-full pl-12 pr-12 py-4 bg-zinc-900 border rounded-2xl text-white text-lg focus:outline-none transition-colors ${searchMode === 'ai' ? 'border-blue-500/50 focus:border-blue-500' : 'border-zinc-700 focus:border-amber-500'}`} autoFocus data-testid="search-input" />
              <button type="button" onClick={() => setSearchOpen(false)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </form>
            {searchQuery.length >= 2 && (
              <div className="mt-2 bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden">
                {searching ? (
                  <div className="p-4 text-center text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />{searchMode === 'ai' ? t('aiAnalyzing') : t('searching')}</div>
                ) : searchResults.length > 0 ? (
                  <div className="divide-y divide-zinc-800">
                    {searchResults.map((item, idx) => (
                      item.is_external ? (
                        <a key={idx} href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 hover:bg-zinc-800/50 transition-colors" onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); }}>
                          <img src={item.image_url} alt={item.title} className="w-16 h-12 object-cover rounded-lg" onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200'} />
                          <div className="flex-1 min-w-0"><h4 className="text-white font-medium line-clamp-1">{item.title}</h4><p className="text-blue-400 text-xs">{item.category} - AI Sonuç</p></div>
                        </a>
                      ) : (
                        <Link key={item.id} to={`/haber/${item.slug || item.id}`} onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); }} className="flex items-center gap-4 p-4 hover:bg-zinc-800/50 transition-colors">
                          <img src={item.image_url} alt={item.title} className="w-16 h-12 object-cover rounded-lg" onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200'} />
                          <div className="flex-1 min-w-0"><h4 className="text-white font-medium line-clamp-1">{item.title}</h4><p className="text-zinc-500 text-sm">{item.category}</p></div>
                        </Link>
                      )
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-zinc-400">{t('noResults')}</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
